import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Autocomplete, TextField, Button } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { Prompt } from 'react-router-dom';
import { RightsContext } from 'providers/RightsContext';
import curry from 'lodash.curry';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import moment from 'moment';
import withCustomComponent from 'providers/withCustomComponent';
import { ParamsContext } from 'providers/ParamsContext';
import { get } from '@totalsoft/change-tracking-react';
import { emptyString } from 'constants/emptyConst';
import { interestType } from 'features/offer/constants';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';

export const OfferAdditionalDataComponent = React.memo(
  ({
    t,
    onPropertyChange,
    invoicingExchangeRates,
    acquisitionExchangeRates,
    creditFinancingLines,
    scheduleTypes,
    offerTypes,
    bankList,
    offerClientFacilities,
    loadClientFacilities,
    onSave,
    saving,
    interestTypes,
    isDirty,
    variableInterestComputing,
    offerFinancialProductRuleReadOnlyFields,
    validation,
    onInterestVariablePercentRefresh,
    lens
  }) => {
    const fixedInterestId = interestTypes?.find(int => int.code === interestType.FIX)?.id;
    const variableInterestId = interestTypes.find(int => int.code === interestType.VAR)?.id;

    const {
      readOnlyFull,
      financialAdditionalDataWrite,
      financialAdditionalDataRead,
      isImage,
      clientInvoicingRead,
      clientInvoicingWrite,
      supplierInvoicingRead,
      supplierInvoicingWrite
    } = useContext(RightsContext);
    const { cvsChPercent } = useContext(ParamsContext);

    const isValidDate = current => {
      if (current) {
        const currentDate = moment();
        return current.isBefore(currentDate);
      } else {
        return true;
      }
    };

    const handlePropertyChange = curry((prop, value) => {
      onPropertyChange(prop, value);
    });

    const handleClientFacilityChange = e => {
      onPropertyChange('clientFacilityId', e.chDocumentId, 'clientFacilitySiteId', e.chSiteId);
    };

    return (
      <>
        <Prompt when={isDirty} message={() => t('LeavingWithoutSaving')} />
        <Grid container spacing={4} px={1}>
          {financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                id={'offerAdditionalDataCard_interestType'}
                disabled={(lens.additionalData.interestReadOnly |> get) || readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={lens.additionalData.isFixed |> get ? fixedInterestId ?? ' ' : variableInterestId ?? ' '}
                valueKey='id'
                labelKey='name'
                label={t('OfferEdit.AdditionalData.InterestType')}
                options={interestTypes}
                onChange={handlePropertyChange('isFixed')}
                isClearable={false}
                simpleValue
              />
            </Grid>
          )}
          {!(lens.additionalData.isFixed |> get) && financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                id={'offerAdditionalDataCard_referenceInterestType'}
                disabled={(lens.additionalData.interestReadOnly |> get) || readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={(lens.additionalData.interestTypeId |> get) || emptyString}
                valueKey='interestTypeId'
                labelKey='interestTypeName'
                label={t('OfferEdit.AdditionalData.ReferenceInterestType')}
                options={lens.filteredOfferReferenceInterests |> get}
                onChange={handlePropertyChange('interestTypeId')}
                isClearable={false}
                simpleValue={true}
              />
            </Grid>
          )}
          {variableInterestComputing && (
            <Grid item xs={12} sm={6} md={4} lg={3} display='flex' alignItems='center'>
              <WrappedLinearProgress />
            </Grid>
          )}
          {!(variableInterestComputing || (lens.additionalData.isFixed |> get)) && financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                id={'offerAdditionalDataCard_interestVariablePercent'}
                disabled={!financialAdditionalDataWrite}
                fullWidth
                isNumeric
                value={
                  typeof (lens.additionalData.interestVariablePercent |> get) === 'number'
                    ? lens.additionalData.interestVariablePercent |> get
                    : ''
                }
                inputProps={{
                  decimalScale: cvsChPercent,
                  refreshOption: {
                    handleRefreshClick: onInterestVariablePercentRefresh,
                    disabled: saving || readOnlyFull
                  }
                }}
                onChange={handlePropertyChange('interestVariablePercent')}
                label={t('OfferEdit.AdditionalData.InterestVariablePercent')}
              />
            </Grid>
          )}
          {!(lens.additionalData.isFixed |> get) && financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateTime
                id={'offerAdditionalDataCard_variableInterestDate'}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={lens.additionalData.variableInterestDate |> get}
                label={t('OfferEdit.AdditionalData.VariableInterestDate')}
                onChange={handlePropertyChange('variableInterestDate')}
                isValidDate={isValidDate}
                error={!isValid(validation.variableInterestDate)}
                helperText={getErrors(validation.variableInterestDate)}
              />
            </Grid>
          )}
          {!(lens.additionalData.hideIIR |> get) && financialAdditionalDataRead && (
            <React.Fragment>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  id={'offerAdditionalDataCard_internalInterestRate'}
                  fullWidth
                  isNumeric
                  value={
                    typeof (lens.additionalData.internalInterestRate |> get) === 'number'
                      ? lens.additionalData.internalInterestRate |> get
                      : ''
                  }
                  label={t('OfferEdit.AdditionalData.InternalInterestRate')}
                  onChange={handlePropertyChange('internalInterestRate')}
                  disabled={readOnlyFull || !financialAdditionalDataWrite}
                  inputProps={{
                    decimalScale: cvsChPercent
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  id={'offerAdditionalDataCard_adjustedInternalInterestRate'}
                  fullWidth
                  isNumeric
                  value={
                    typeof (lens.additionalData.adjustedInternalInterestRate |> get) === 'number'
                      ? lens.additionalData.adjustedInternalInterestRate |> get
                      : ''
                  }
                  label={t('OfferEdit.AdditionalData.AdjustedInternalInterestRate')}
                  onChange={handlePropertyChange('adjustedInternalInterestRate')}
                  disabled={readOnlyFull || !financialAdditionalDataWrite}
                  inputProps={{
                    decimalScale: cvsChPercent
                  }}
                />
              </Grid>
            </React.Fragment>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <TextField
                id={'offerAdditionalDataCard_dae'}
                fullWidth
                isNumeric
                value={typeof (lens.additionalData.dae |> get) === 'number' ? lens.additionalData.dae |> get : ''}
                onChange={handlePropertyChange('dae')}
                label={t('OfferEdit.AdditionalData.Dae')}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
                inputProps={{
                  decimalScale: cvsChPercent
                }}
              />
            </Grid>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                id={'offerAdditionalDataCard_margin'}
                disabled={
                  readOnlyFull || !financialAdditionalDataWrite || offerFinancialProductRuleReadOnlyFields?.interestFixedPercentReadOnly
                }
                fullWidth
                isNumeric
                value={typeof (lens.additionalData.margin |> get) === 'number' ? lens.additionalData.margin |> get : ''}
                onChange={handlePropertyChange('margin')}
                label={
                  lens.additionalData.isFixed |> get
                    ? t('OfferEdit.AdditionalData.InterestRateFixed')
                    : t('OfferEdit.AdditionalData.Margin')
                }
                inputProps={{
                  decimalScale: cvsChPercent
                }}
              />
            </Grid>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                id={'offerAdditionalDataCard_scheduleDueDay'}
                disabled={readOnlyFull || !financialAdditionalDataWrite || offerFinancialProductRuleReadOnlyFields.scheduleDueDayReadOnly}
                fullWidth
                isNumeric
                inputProps={{ decimalScale: 0 }}
                value={typeof (lens.additionalData.scheduleDueDay |> get) === 'number' ? lens.additionalData.scheduleDueDay |> get : ''}
                onChange={handlePropertyChange('scheduleDueDay')}
                label={t('OfferEdit.AdditionalData.ScheduleDueDay')}
              />
            </Grid>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={3}>
              <Autocomplete
                id={'offerAdditionalDataCard_invoicingExchangeRate'}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={lens.additionalData.invoicingExchangeRateId |> get || emptyString}
                valueKey='invoicingExchangeRateId'
                labelKey='invoicingExchangeRateName'
                label={t('OfferEdit.AdditionalData.InvoicingExchangeRate')}
                options={invoicingExchangeRates}
                onChange={handlePropertyChange('invoicingExchangeRateId')}
                isClearable={false}
                simpleValue={true}
              />
            </Grid>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={3}>
              <Autocomplete
                id={'offerAdditionalDataCard_acquisitionExchangeRate'}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={lens.additionalData.acquisitionExchangeRateId |> get || emptyString}
                valueKey='acquisitionExchangeRateId'
                labelKey='acquisitionExchangeRateName'
                label={t('OfferEdit.AdditionalData.AcquisitionExchangeRate')}
                options={acquisitionExchangeRates}
                onChange={handlePropertyChange('acquisitionExchangeRateId')}
                isClearable={false}
                simpleValue={true}
              />
            </Grid>
          )}
          {clientInvoicingRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <Autocomplete
                id={'offerAdditionalDataCard_clientInvoicingBank'}
                disabled={readOnlyFull || !clientInvoicingWrite}
                fullWidth
                value={lens.additionalData.clientInvoicingBankId |> get || emptyString}
                valueKey='bankId'
                labelKey='bankName'
                label={t('OfferEdit.AdditionalData.ClientInvoicingBank')}
                options={bankList}
                onChange={handlePropertyChange('clientInvoicingBankId')}
                isClearable={false}
                simpleValue={true}
              />
            </Grid>
          )}
          {clientInvoicingRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <TextField
                id={'offerAdditionalDataCard_clientConversionFactor'}
                fullWidth
                isNumeric
                value={
                  typeof (lens.additionalData.clientConversionFactor |> get) === 'number'
                    ? lens.additionalData.clientConversionFactor |> get
                    : ''
                }
                onChange={handlePropertyChange('clientConversionFactor')}
                label={t('OfferEdit.AdditionalData.ClientConversionFactoraaaaa')}
                disabled={readOnlyFull || !clientInvoicingWrite}
              />
            </Grid>
          )}
          {supplierInvoicingRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <Autocomplete
                id={'offerAdditionalDataCard_supplierInvoicingBank'}
                fullWidth
                value={lens.additionalData.supplierInvoicingBankId |> get || emptyString}
                valueKey='bankId'
                labelKey='bankName'
                label={t('OfferEdit.AdditionalData.SupplierInvoicingBank')}
                options={bankList}
                onChange={handlePropertyChange('supplierInvoicingBankId')}
                isClearable={false}
                simpleValue={true}
                disabled={readOnlyFull || !supplierInvoicingWrite}
              />
            </Grid>
          )}
          {supplierInvoicingRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <TextField
                id={'offerAdditionalDataCard_supplierConversionFactor'}
                fullWidth
                isNumeric
                value={
                  typeof (lens.additionalData.supplierConversionFactor |> get) === 'number'
                    ? lens.additionalData.supplierConversionFactor |> get
                    : ''
                }
                onChange={handlePropertyChange('supplierConversionFactor')}
                label={t('OfferEdit.AdditionalData.SupplierConversionFactor')}
                disabled={readOnlyFull || !supplierInvoicingWrite}
                error={!isValid(validation.supplierConversionFactor)}
                helperText={getErrors(validation.supplierConversionFactor)}
              />
            </Grid>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={3}>
              <Autocomplete
                id={'offerAdditionalDataCard_creditFinancingLines'}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={lens.additionalData.creditFinancingLine |> get || {}}
                valueKey='creditFinancingLineId'
                labelKey='creditFinancingLineNumber'
                label={t('OfferEdit.AdditionalData.CreditFinancingLines')}
                options={creditFinancingLines}
                onChange={handlePropertyChange('creditFinancingLine')}
                isClearable={false}
              />
            </Grid>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={6}>
              <Autocomplete
                id={'offerAdditionalDataCard_scheduleType'}
                disabled={!financialAdditionalDataWrite}
                fullWidth
                value={lens.additionalData.scheduleTypeId |> get || emptyString}
                valueKey='scheduleTypeId'
                labelKey='scheduleTypeName'
                label={t('OfferEdit.AdditionalData.ScheduleType')}
                options={scheduleTypes}
                onChange={handlePropertyChange('scheduleTypeId')}
                isClearable={false}
                simpleValue={true}
              />
            </Grid>
          )}
          {financialAdditionalDataRead && (
            <Grid item xs={3}>
              <Autocomplete
                id={'offerAdditionalDataCard_offerType'}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={lens.additionalData.offerTypeId |> get || emptyString}
                valueKey='offerTypeId'
                labelKey='offerTypeName'
                label={t('OfferEdit.AdditionalData.OfferType')}
                options={offerTypes}
                onChange={handlePropertyChange('offerTypeId')}
                isClearable={false}
                simpleValue={true}
              />
            </Grid>
          )}
          {!(lens.additionalData.hideRORAC |> get) && financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <Autocomplete
                id={'offerAdditionalDataCard_facility'}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
                fullWidth
                value={{
                  chDocumentId: lens.additionalData.clientFacilityId |> get,
                  documentNumber: lens.additionalData.clientFacilityNumber |> get
                }}
                valueKey='chDocumentId'
                labelKey='documentNumber'
                label={t('OfferEdit.AdditionalData.ClientFacility')}
                options={offerClientFacilities.facilityList}
                onChange={e => handleClientFacilityChange(e)}
                isClearable={false}
                isLoading={offerClientFacilities.loading}
                onOpen={loadClientFacilities}
              />
            </Grid>
          )}
          {!(lens.additionalData.hideRORAC |> get) && financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <TextField
                id={'offerAdditionalDataCard_rorac'}
                fullWidth
                isNumeric
                value={typeof (lens.additionalData.rorac |> get) === 'number' ? lens.additionalData.rorac |> get : ''}
                onChange={handlePropertyChange('rorac')}
                label={t('OfferEdit.AdditionalData.Rorac')}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
              />
            </Grid>
          )}
          {!(lens.additionalData.hideRORAC |> get) && financialAdditionalDataRead && (
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'none' }}>
              <TextField
                id={'offerAdditionalDataCard_clientFacilityRorac'}
                fullWidth
                isNumeric
                value={
                  typeof (lens.additionalData.clientFacilityRORAC |> get) === 'number' ? lens.additionalData.clientFacilityRORAC |> get : ''
                }
                label={t('OfferEdit.AdditionalData.ClientFacilityRorac')}
                onChange={handlePropertyChange('clientFacilityRorac')}
                disabled={readOnlyFull || !financialAdditionalDataWrite}
              />
            </Grid>
          )}
          {!isImage && (
            <GridActionsWrapper item xs={12}>
              <Button
                id={'offerAdditionalDataCard_saveBtn'}
                color='primary'
                variant='contained'
                onClick={onSave}
                disabled={
                  readOnlyFull || saving || !isDirty || !(financialAdditionalDataWrite || clientInvoicingWrite || supplierInvoicingWrite)
                }
              >
                {saving ? t('Saving') + '...' : t('Save')}
              </Button>
            </GridActionsWrapper>
          )}
        </Grid>
      </>
    );
  }
);

OfferAdditionalDataComponent.displayName = 'OfferAdditionalDataComponent';

OfferAdditionalDataComponent.propTypes = {
  t: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  invoicingExchangeRates: PropTypes.array.isRequired,
  acquisitionExchangeRates: PropTypes.array.isRequired,
  creditFinancingLines: PropTypes.array.isRequired,
  scheduleTypes: PropTypes.array.isRequired,
  interestTypes: PropTypes.array.isRequired,
  offerTypes: PropTypes.array.isRequired,
  bankList: PropTypes.array.isRequired,
  offerClientFacilities: PropTypes.array.isRequired,
  loadClientFacilities: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  variableInterestComputing: PropTypes.bool.isRequired,
  offerFinancialProductRuleReadOnlyFields: PropTypes.object.isRequired,
  validation: PropTypes.object,
  onInterestVariablePercentRefresh: PropTypes.func.isRequired,
  lens: PropTypes.object.isRequired
};

export default compose(withTranslation(), withCustomComponent('OfferAdditionalDataComponent'))(OfferAdditionalDataComponent);
